import { useCallback, useEffect, useRef, useState } from 'react'

export interface UseConfirmDialogReturn {
  /** Open the dialog and await the user's choice. Returns true if confirmed. */
  confirm: () => Promise<boolean>
  /** Whether the dialog is currently open. Pass to the dialog component. */
  dialogOpen: boolean
  /**
   * Call with `true` when the user confirms, `false` when they cancel.
   * Resolves the pending promise and closes the dialog.
   */
  onResolve: (confirmed: boolean) => void
}

/**
 * Promise-based confirm dialog hook.
 *
 * Usage:
 * ```ts
 * const { confirm, dialogOpen, onResolve } = useConfirmDialog()
 *
 * // In an event handler (or async callback):
 * if (!(await confirm())) return // user cancelled
 * // proceed...
 *
 * // In JSX:
 * <MyDialog open={dialogOpen} onConfirm={() => onResolve(true)} onCancel={() => onResolve(false)} />
 * ```
 *
 * Unmount safety: if the component unmounts while the dialog is open, the
 * pending promise resolves to `false` so callers never hang on an unresolved
 * promise. This also prevents setState calls on an unmounted component.
 */
export function useConfirmDialog(): UseConfirmDialogReturn {
  const [dialogOpen, setDialogOpen] = useState(false)
  const resolverRef = useRef<((value: boolean) => void) | null>(null)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      // Resolve any pending promise to false to unblock callers on unmount.
      if (resolverRef.current) {
        resolverRef.current(false)
        resolverRef.current = null
      }
    }
  }, [])

  const confirm = useCallback((): Promise<boolean> => {
    return new Promise((resolve) => {
      resolverRef.current = resolve
      setDialogOpen(true)
    })
  }, [])

  const onResolve = useCallback((confirmed: boolean) => {
    if (!mountedRef.current) return
    setDialogOpen(false)
    if (resolverRef.current) {
      resolverRef.current(confirmed)
      resolverRef.current = null
    }
  }, [])

  return { confirm, dialogOpen, onResolve }
}
